import React, { useEffect, useState } from "react";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LightModeIcon from '@mui/icons-material/LightMode';
import List from '@mui/material/List';
import ListIcon from '@mui/icons-material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';

const drawerWidth = 240;
const navItems = [['History', 'history'], ['Expertise', 'expertise'], ['Projects', 'projects'],  ['Leadership', 'leadership'], ['Contact', 'contact']];

function Navigation({parentToChild, modeChange}: any) {

  const {mode} = parentToChild;

  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [clickedItem, setClickedItem] = useState<string | null>(null);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById("navigation");
      if (navbar) {
        const scrolled = window.scrollY > navbar.clientHeight;
        setScrolled(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (section: string) => {
    const element = document.getElementById(section);
    const navbar = document.getElementById("navigation");
    if (element && navbar) {
      const navbarHeight = navbar.offsetHeight;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNavClick = (section: string) => {
    setClickedItem(section);
    scrollToSection(section);
    setTimeout(() => setClickedItem(null), 500);
  };

  const drawer = (
    <Box className="navigation-bar-responsive" onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <p className="mobile-menu-top"><ListIcon/>Menu</p>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item[0]} disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }} onClick={() => handleNavClick(item[1])}>
              <ListItemText primary={item[0]} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding sx={{ mt: 1, px: 2 }}>
          <Button
            component="a"
            href="https://mp0549.github.io/lab/"
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            sx={{
              color: '#fff',
              border: '2px solid #a855f7',
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '1rem',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #5000ca 0%, #a855f7 100%)',
              boxShadow: '0 0 12px #a855f7',
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #c084fc 100%)',
              },
            }}
          >
            Visit The Lab!
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav" id="navigation" className={`navbar-fixed-top${scrolled ? ' scrolled' : ''}`}>
        <Toolbar className='navigation-bar'>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {mode === 'dark' ? (
            <LightModeIcon onClick={() => modeChange()}/>
          ) : (
            <DarkModeIcon onClick={() => modeChange()}/>
          )}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {navItems.map((item) => (
              <Button key={item[0]} onClick={() => handleNavClick(item[1])} sx={{ color: '#fff' }}>
                <span className={`nav-label${clickedItem === item[1] ? ' nav-label--active' : ''}`}>
                  {item[0]}
                </span>
              </Button>
            ))}
            <Button
              component="a"
              href="https://mp0549.github.io/lab/"
              target="_blank"
              rel="noopener noreferrer"
              className="lab-button"
              sx={{
                ml: 1,
                color: '#fff',
                border: '2px solid #a855f7',
                borderRadius: '8px',
                px: 2,
                py: 0.5,
                fontWeight: 'bold',
                fontSize: '1rem',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #5000ca 0%, #a855f7 100%)',
                boxShadow: '0 0 12px #a855f7, 0 0 4px #5000ca',
                animation: 'labPulse 2s ease-in-out infinite',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6d28d9 0%, #c084fc 100%)',
                  boxShadow: '0 0 20px #a855f7, 0 0 8px #5000ca',
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease',
                },
              }}
            >
              Visit The Lab!
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}

export default Navigation;